import React, { useEffect, useState, useRef, useCallback } from "react";
import "../../styles/components/landing-pages/jobPage.scss";
import Navbar from "./Navbar";
import ResumeHighlight from "../../animations/components/ResumeHighlight";
import feature2 from "../../assets/feature2.png";
import feature3 from "../../assets/feature3.png";
import feature4 from "../../assets/feature4.png";
import Footer from "./Footer";
import jobDescriptionIcon from "../../assets/job-details.svg";
import jobTile from "../../assets/job-tile.svg";
import { Link } from "react-router-dom";
import JobTile from "../../animations/components/JobTile";
import NewJobBenefits from "../../animations/components/NewJobBenefits";

const ApplicationTrackerPage = () => {
  const [scrollProgress, setScrollProgress] = useState(0);
  const demoSectionRef = useRef<HTMLDivElement | null>(null);
  const heroSectionRef = useRef<HTMLElement | null>(null);
  const [demoSectionDimensions, setDemoSectionDimensions] = useState({
    width: 0,
    height: 0,
  });
  const [heroHeight, setHeroHeight] = useState(0);
  const NAVBAR_HEIGHT = 88;
  const [isCaptionVisible, setIsCaptionVisible] = useState(false);
  const [hasExpanded, setHasExpanded] = useState(false);
  const [activeGroup, setActiveGroup] = useState<'days5' | 'days10' | 'days15' | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [activeFaqIndex, setActiveFaqIndex] = useState<number | null>(null);

  // Get dimensions after component mounts
  useEffect(() => {
    if (demoSectionRef.current) {
      const rect = demoSectionRef.current.getBoundingClientRect();
      setDemoSectionDimensions({
        width: rect.width,
        height: rect.height,
      });
    }
    if (heroSectionRef.current) {
      const rect = heroSectionRef.current.getBoundingClientRect();
      setHeroHeight(rect.height);
    }
  }, []);

  // Handle scroll
  useEffect(() => {
    const handleScroll = () => {
      if (demoSectionRef.current) {
        const rect = demoSectionRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        
        const startScroll = rect.top - viewportHeight * 1.8;
        const endScroll = rect.top + (viewportHeight * 0.8);
        const scrollRange = endScroll - startScroll;
        
        const currentScroll = window.scrollY - startScroll;
        const progress = Math.max(0, Math.min(1, currentScroll / scrollRange));
        
        setScrollProgress(progress);
        
        if (progress >= 1 && !hasExpanded) {
          setHasExpanded(true);
        }
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    requestAnimationFrame(handleScroll);
    
    return () => window.removeEventListener("scroll", handleScroll);
  }, [hasExpanded]);

  // Calculate transforms
  const progress = scrollProgress || 0;

  // For hero section
  const heroTranslateY = -progress * 100;
  const heroOpacity = Math.max(1 - progress * 2, 0);

  // For demo section
  const maxScaleX = Math.min(5, window.innerWidth / (180 * 2)); // Using fixed base width
  const maxScaleY = Math.min(5, (window.innerHeight - NAVBAR_HEIGHT) / (180 * 2));
  const maxScale = Math.min(5, maxScaleX, maxScaleY); // Limit max scale to 5x

  const demoScale = 1 + (progress * (maxScale - 1)); // This makes it scale up smoothly
  const deltaHeight = demoSectionDimensions.height * (demoScale - 1);
  const demoTranslateY = progress * -50;

  useEffect(() => {
    if (progress >= 1 && !isCaptionVisible) {
      setIsCaptionVisible(true);
    }
  }, [progress]);

  useEffect(() => {
    if (progress >= 1 && !hasExpanded) {
      setHasExpanded(true);
    }
  }, [progress]);

  const calculateProgress = useCallback(() => {
    if (!demoSectionRef.current) return 0;
    
    const rect = demoSectionRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight - NAVBAR_HEIGHT;
    const elementTop = rect.top - NAVBAR_HEIGHT;
    
    const progress = Math.max(0, Math.min(1, 1 - (elementTop / viewportHeight)));
    
    return progress;
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // Set up intersection observer for feature animations
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Add animate class immediately
          entry.target.classList.add('animate');
          
          // Force a reflow
          if (entry.target instanceof HTMLElement) {
            void entry.target.offsetHeight;
          }
          
          if (entry.isIntersecting) {
            // Add visible class after a small delay to ensure animation triggers
            requestAnimationFrame(() => {
              entry.target.classList.add('visible');
            });
            observer.unobserve(entry.target); // Only animate once
          }
        });
      },
      {
        root: null,
        threshold: 0.2, // Start animation when 20% of element is visible
        rootMargin: '0px'
      }
    );

    // Observe all feature rows
    document.querySelectorAll('.feature-row').forEach((feature) => {
      observer.observe(feature);
    });

    return () => observer.disconnect();
  }, []);

  const handleFaqClick = (index: number) => {
    setActiveFaqIndex(activeFaqIndex === index ? null : index);
  };

  // Handle entrance animations for FAQs
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          entry.target.classList.add('animate');
          
          if (entry.target instanceof HTMLElement) {
            void entry.target.offsetHeight;
          }
          
          if (entry.isIntersecting) {
            requestAnimationFrame(() => {
              entry.target.classList.add('visible');
            });
            observer.unobserve(entry.target);
          }
        });
      },
      {
        root: null,
        threshold: 0.2,
        rootMargin: '0px'
      }
    );

    document.querySelectorAll('.faq-item').forEach((item) => {
      observer.observe(item);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <Navbar />
      <div className="job-page-container">
        <section
          ref={heroSectionRef}
          className="job-features-hero-section animate"
        >
          <div className="hero-content">
            <h1 className="hero-heading animate">
            Effortless Job Application Tracking
            </h1>
            <p className="hero-subheading animate">
            Stay on top of your job search with our intuitive tracking system. Monitor applications, 
            manage follow-ups, and keep all your progress in one convenient place.
            </p>
            <Link to="/auth/signup" className="cta-btn animate">
              Get Started - It's Free
            </Link>
          </div>
          <div className="hero-image-container">
            <div className="resume-highlight-container">
              <ResumeHighlight />
            </div>
          </div>
        </section>

        <section className="job-demo-section" ref={demoSectionRef}>
          <div className="demo-container">
            <h2 className="demo-heading">Smart Application Management</h2>
            <div className="demo-animation-container">
              <img
                src={jobDescriptionIcon}
                alt="Application Tracker Demo"
                className={`demo-icon ${hasExpanded && !isMobile ? 'expanded' : ''}`}
                style={!hasExpanded && !isMobile ? { 
                  '--scale': `${1 + (progress * 5.67)}`
                } as React.CSSProperties : {}}
              />
            </div>
          </div>
        </section>

        <section className="job-features-section">
          <div className="feature-row">
            <div className="landing-feature-content">
              <h2>Centralized Application Management</h2>
              <p>
              Stay organized with a centralized dashboard that lets you manage your job 
              applications effortlessly. Track statuses, deadlines, and follow-ups all 
              through the platform.
              </p>
            </div>
            <div className="feature-image">
              <NewJobBenefits />
            </div>
          </div>

          <div className="feature-row">
            <div className="feature-image">
              <img src={feature2} alt="Reminders feature" />
            </div>
            <div className="landing-feature-content">
              <h2>Automated Status Updates</h2>
              <p>
              Never miss a beat with intelligent reminders for interviews, deadlines, and follow-ups. Stay 
              prepared and leave a lasting impression on your interviewers.
              </p>
            </div>
          </div>

          <div className="feature-row">
            <div className="landing-feature-content">
              <h2>Performance Analytics</h2>
              <p>
              Stay informed with detailed analytics on your job search. Track 
              success rates, visualize progress, and pinpoint areas to enhance your strategy.
              </p>
            </div>
            <div className="feature-image">
              <JobTile />
            </div>
          </div>

          <div className="feature-row">
            <div className="feature-image">
              <img src={feature4} alt="Notes feature" />
            </div>
            <div className="landing-feature-content">
              <h2>Follow-up Reminders</h2>
              <p>
              Keep everything in one place—company research, interview questions, and key contacts. Stay 
              prepared and organized for every application.
              </p>
            </div>
          </div>
        </section>

        <section className="faq-section">
          <div className="faq-container">
            <h2 className="faq-heading">Frequently Asked Questions</h2>
            <div className="faq-grid">
              {[
                {
                  question: "How does the application tracker work?",
                  answer: "Our tracker automatically organizes your job applications, deadlines, and follow-ups in one place. It syncs with your email to track application status and interview schedules automatically."
                },
                {
                  question: "Can I track applications from different job boards?",
                  answer: "Yes! Our tracker works with all major job boards and company career sites. You can even manually add applications from any source to keep everything organized."
                },
                {
                  question: "What metrics and insights are available?",
                  answer: "Track application success rates, interview conversion rates, response times, and more. Get insights on which job sources and resume versions perform best."
                },
                {
                  question: "Does it send reminders for follow-ups?",
                  answer: "Yes, receive smart reminders for follow-ups, interview preparation, and thank you notes. Customize reminder timing to match your preferences."
                },
                {
                  question: "Can I collaborate with my career coach?",
                  answer: "Yes, share your application tracking dashboard with career coaches or mentors to get feedback and guidance on your job search strategy."
                },
                {
                  question: "Is there a limit to tracked applications?",
                  answer: "No limits! Track as many applications as you need. Our platform scales with your job search, whether you're tracking 5 or 500 applications."
                }
              ].map((faq, index) => (
                <div 
                  key={index}
                  className={`faq-item ${activeFaqIndex === index ? 'active' : ''}`}
                >
                  <div 
                    className="faq-question"
                    onClick={() => handleFaqClick(index)}
                  >
                    <h3>{faq.question}</h3>
                    <span className="faq-icon">+</span>
                  </div>
                  <div className="faq-answer">
                    <p>{faq.answer}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default ApplicationTrackerPage; 